<template>
  <div class="asset-details-sidebar" :class="{ 'sidebar-open': isOpen }">
    <div class="sidebar-header">
      <h5 class="sidebar-title">{{ getTitle() }}</h5>
      <button class="btn-close" @click="closeSidebar">
        <i class="pi pi-times"></i>
      </button>
    </div>

    <div class="sidebar-content" v-if="selectedAsset">
      <!-- Card 9 Content: Insured Assets -->
      <div v-if="selectedAsset.cardType === 'card9'" class="table-container">
        <div class="table-section">
          <h6 class="table-title">Insured Assets</h6>
          <NormalTable
            :columns="insuranceHeaders"
            v-model:value="insuranceData"
            :rowHover="true"
            filterDisplay="menu"
            responsiveLayout="scroll"
            :paginator="true"
          >
            <template v-slot:Action="{ data }">
              <div class="action-buttons">
                <button
                  class="btn btn-sm btn-primary me-1"
                  @click="viewInsuranceDetails(data)"
                >
                  <i class="pi pi-eye"></i> View Details
                </button>
                <button
                  class="btn btn-sm btn-secondary"
                  @click="updatePolicy(data)"
                >
                  <i class="pi pi-refresh"></i> Update Policy
                </button>
              </div>
            </template>
          </NormalTable>
        </div>
      </div>

      <!-- Card 7 Content: Expiring Warranties -->
      <div
        v-else-if="selectedAsset.cardType === 'card7'"
        class="table-container"
      >
        <div class="table-section">
          <h6 class="table-title">Expiring Warranties</h6>
          <NormalTable
            :columns="warrantyHeaders"
            v-model:value="warrantyData"
            :rowHover="true"
            filterDisplay="menu"
            responsiveLayout="scroll"
            :paginator="true"
          >
            <template v-slot:Action="{ data }">
              <div class="action-buttons">
                <button
                  class="btn btn-sm btn-primary me-1"
                  @click="viewWarrantyDetails(data)"
                >
                  <i class="pi pi-eye"></i> View Details
                </button>
                <button
                  class="btn btn-sm btn-secondary"
                  @click="extendWarranty(data)"
                >
                  <i class="pi pi-calendar-plus"></i> Extend Warranty
                </button>
              </div>
            </template>
          </NormalTable>
        </div>
      </div>

      <!-- Card 5 Content: Assets in Transit -->
      <div
        v-else-if="selectedAsset.cardType === 'card5'"
        class="table-container"
      >
        <div class="table-section">
          <h6 class="table-title">Assets in Transit</h6>
          <NormalTable
            :columns="transitHeaders"
            v-model:value="transitData"
            :rowHover="true"
            filterDisplay="menu"
            responsiveLayout="scroll"
            :paginator="true"
          >
            <template v-slot:Action="{ data }">
              <div class="action-buttons">
                <button
                  class="btn btn-sm btn-primary me-1"
                  @click="viewAssetDetails(data)"
                >
                  <i class="pi pi-eye"></i> View Details
                </button>
                <button
                  class="btn btn-sm btn-secondary"
                  @click="manageTransfer(data)"
                >
                  <i class="pi pi-cog"></i> Manage Transfer
                </button>
              </div>
            </template>
          </NormalTable>
        </div>
      </div>

      <!-- Card 3 Content: Asset Availability Analysis -->
      <div
        v-else-if="selectedAsset.cardType === 'card3'"
        class="table-container"
      >
        <div class="table-section">
          <h6 class="table-title">Asset Availability by Department</h6>
          <NormalTable
            :columns="departmentHeaders"
            v-model:value="departmentData"
            :rowHover="true"
            filterDisplay="menu"
            responsiveLayout="scroll"
            :paginator="true"
          >
            <template v-slot:Action="{ data }">
              <div class="action-buttons">
                <button
                  class="btn btn-sm btn-primary me-1"
                  @click="viewAssets(data)"
                >
                  <i class="pi pi-eye"></i> View
                </button>
                <button
                  class="btn btn-sm btn-secondary"
                  @click="reallocateAssets(data)"
                >
                  <i class="pi pi-refresh"></i> Reallocate
                </button>
              </div>
            </template>
          </NormalTable>
        </div>
        <div class="table-section mt-4">
          <h6 class="table-title">Asset Availability by Location</h6>
          <NormalTable
            :columns="locationHeaders"
            v-model:value="locationData"
            :rowHover="true"
            filterDisplay="menu"
            responsiveLayout="scroll"
            :paginator="true"
          >
            <template v-slot:Action="{ data }">
              <div class="action-buttons">
                <button
                  class="btn btn-sm btn-primary"
                  @click="viewLocationAssets(data)"
                >
                  <i class="pi pi-eye"></i> View Assets
                </button>
              </div>
            </template>
          </NormalTable>
        </div>

        <div class="table-section mt-4">
          <h6 class="table-title">Assets by Category</h6>
          <div class="category-chart-data">
            <div
              class="category-item"
              v-for="category in categoryData"
              :key="category.Category"
            >
              <div class="category-info">
                <span class="category-name">{{ category.Category }}</span>
                <span class="category-percentage"
                  >{{ category.AssetPercentage }}%</span
                >
              </div>
              <ProgressBar
                :value="category.AssetPercentage"
                :style="{ height: '12px', marginTop: '8px' }"
                :showValue="false"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Card 1 Content: Total Assets Overview -->
      <div
        v-else-if="selectedAsset.cardType === 'card1'"
        class="overview-content"
      >
        <div class="overview-stats">
          <div class="stat-card">
            <h6>Total Assets</h6>
            <p class="stat-number">{{ DetailsData?.TotalAsset }}</p>
          </div>
          <div class="stat-card">
            <h6>Assigned Assets</h6>
            <p class="stat-number">{{ DetailsData?.AssetInUse }}</p>
          </div>
          <div class="stat-card">
            <h6>UnAssign Assets</h6>
            <p class="stat-number">{{ DetailsData?.TotalUnAssignAsset }}</p>
          </div>
        </div>
      </div>

      <!-- Card 2 Content: Assets In Use -->
      <div v-else-if="selectedAsset.cardType === 'card2'" class="inuse-content">
        <div class="inuse-stats">
          <div class="stat-card">
            <h6>Currently In Use</h6>
            <p class="stat-number">{{ DetailsData?.AssetInUse }}</p>
          </div>
          <div class="stat-card">
            <h6>Utilization Rate</h6>
            <p class="stat-number">
              {{ DetailsData?.UtilizationRateFormatted }}
            </p>
          </div>
        </div>
      </div>

      <!-- Default Asset Details Content -->
      <div v-else class="asset-info-section">
        <h6 class="section-title">Basic Information</h6>
        <div class="info-item">
          <label>Asset ID:</label>
          <span>{{ selectedAsset.assetId || "N/A" }}</span>
        </div>
        <div class="info-item">
          <label>Asset Name:</label>
          <span>{{ selectedAsset.assetName || "N/A" }}</span>
        </div>
        <div class="info-item">
          <label>Category:</label>
          <span>{{ selectedAsset.category || "N/A" }}</span>
        </div>
        <div class="info-item">
          <label>Status:</label>
          <span
            class="status-badge"
            :class="getStatusClass(selectedAsset.status)"
          >
            {{ selectedAsset.status || "N/A" }}
          </span>
        </div>

        <div class="asset-info-section">
          <h6 class="section-title">Location & Assignment</h6>
          <div class="info-item">
            <label>Current Location:</label>
            <span>{{ selectedAsset.location || "N/A" }}</span>
          </div>
          <div class="info-item">
            <label>Assigned To:</label>
            <span>{{ selectedAsset.assignedTo || "Unassigned" }}</span>
          </div>
          <div class="info-item">
            <label>Department:</label>
            <span>{{ selectedAsset.department || "N/A" }}</span>
          </div>
        </div>

        <div class="sidebar-actions">
          <button class="btn btn-primary btn-sm me-2">
            <i class="pi pi-pencil me-1"></i>
            Edit Asset
          </button>
          <button class="btn btn-outline-secondary btn-sm">
            <i class="pi pi-eye me-1"></i>
            View History
          </button>
        </div>
      </div>
    </div>

    <div class="sidebar-content" v-else>
      <div class="no-selection">
        <i class="pi pi-info-circle"></i>
        <p>Select an asset to view details</p>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter, useRoute } from "vue-router";
import NormalTable from "../../../../../../../components/FormComponents/NormalTable.vue";
import ProgressBar from "primevue/progressbar";
import AssetMasterService from "../../../../../../../modules/Inventory/Service/AssetManagement/AssetMasterService";

interface AssetDetails {
  assetId?: string;
  assetName?: string;
  category?: string;
  status?: string;
  location?: string;
  assignedTo?: string;
  department?: string;
  purchaseDate?: string;
  purchaseCost?: number;
  currentValue?: number;
  lastMaintenance?: string;
  nextMaintenance?: string;
  warrantyExpiry?: string;
  cardType?: string;
  data?: any;
}

export default defineComponent({
  name: "AssetDetailsRightSidebar",
  components: {
    NormalTable,
    ProgressBar,
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    selectedAsset: {
      type: Object as PropType<AssetDetails>,
      default: null,
    },
  },
  emits: ["close"],
  setup(props, { emit }) {
    const { t } = useI18n();
    const routes = useRoute();
    const DetailsData = ref();
    const DetailsData1 = ref();
    let SessionUserData = localStorage.getItem("UsersData");
    let usersData;
    if (SessionUserData !== null) {
      usersData = JSON.parse(SessionUserData);
    }
    let HospitalId = Number(usersData.OrganizationId);

    const ParamValues = ref({
      StoreId: Number(routes.query.Id ?? 0),
      BranchId: Number(routes.query.BranchId ?? 0),
    });

    onMounted(() => {
      // alert("asss");
      IsShow();
    });

    const insuranceHeaders = [
      {
        header: "Asset Name",
        field: "AssetName",
        filterTemplate: "MultiSelect",
        ShowMactchMode: false,
        class: "mediumwidth",
      },
      {
        header: "Insurance Provider",
        field: "InsuranceProvider",
        filterTemplate: "MultiSelect",
        ShowMactchMode: false,
        class: "mediumwidth",
      },
      {
        header: "Policy Expiration Date",
        field: "ExpirationDate",
        filterTemplate: "MultiSelect",
        ShowMactchMode: false,
        class: "mediumwidth",
      },
      {
        header: "Policy Number",
        field: "PolicyNumber",
        filterTemplate: "MultiSelect",
        ShowMactchMode: false,
        class: "mediumwidth",
      },
      // {
      //   header: "Action",
      //   field: "action",
      //   slot: "Action",
      //   frozencolumn: true,
      //   sortable: false,
      //   class: "filterdisplaynone last-column-position",
      //   fieldDisplay: "false",
      // },
    ];

    const warrantyHeaders = [
      {
        header: "Asset Name",
        field: "AssetName",
        filterTemplate: "MultiSelect",
        ShowMactchMode: false,
        class: "mediumwidth",
      },
      {
        header: "Expiration Date",
        field: "ExpirationDate",
        filterTemplate: "MultiSelect",
        ShowMactchMode: false,
        class: "mediumwidth",
      },
      {
        header: "Department",
        field: "Department",
        filterTemplate: "MultiSelect",
        ShowMactchMode: false,
        class: "mediumwidth",
      },
      {
        header: "Warranty Provider",
        field: "WarrantyProvider",
        filterTemplate: "MultiSelect",
        ShowMactchMode: false,
        class: "mediumwidth",
      },
      // {
      //   header: "Action",
      //   field: "action",
      //   slot: "Action",
      //   frozencolumn: true,
      //   sortable: false,
      //   class: "filterdisplaynone last-column-position",
      //   fieldDisplay: "false",
      // },
    ];

    const transitHeaders = [
      {
        header: "Asset Name",
        field: "AssetName",
        filterTemplate: "MultiSelect",
        ShowMactchMode: false,
        class: "mediumwidth",
      },
      {
        header: "From Location",
        field: "FromLocation",
        filterTemplate: "MultiSelect",
        ShowMactchMode: false,
        class: "mediumwidth",
      },
      {
        header: "To Location",
        field: "ToLocation",
        filterTemplate: "MultiSelect",
        ShowMactchMode: false,
        class: "mediumwidth",
      },
      {
        header: "Transfer Date",
        field: "TransferDate",
        filterTemplate: "MultiSelect",
        ShowMactchMode: false,
        class: "mediumwidth",
      },
      {
        header: "Expected Arrival Date",
        field: "expectedArrivalDate",
        filterTemplate: "MultiSelect",
        ShowMactchMode: false,
        class: "mediumwidth",
      },
      // {
      //   header: "Action",
      //   field: "action",
      //   slot: "Action",
      //   frozencolumn: true,
      //   sortable: false,
      //   class: "filterdisplaynone last-column-position",
      //   fieldDisplay: "false",
      // },
    ];

    const departmentHeaders = [
      {
        header: "Department",
        field: "StoreName",
        filterTemplate: "MultiSelect",
        ShowMactchMode: false,
        class: "mediumwidth",
      },
      {
        header: "Total Assets",
        field: "TotalAssetCount",
        filterTemplate: "MultiSelect",
        ShowMactchMode: false,
        class: "smallwidth",
      },
      {
        header: "In Use",
        field: "InUseCount",
        filterTemplate: "MultiSelect",
        ShowMactchMode: false,
        class: "smallwidth",
      },
      {
        header: "Available",
        field: "AssetAvailabilityCount",
        filterTemplate: "MultiSelect",
        ShowMactchMode: false,
        class: "smallwidth",
      },
      {
        header: "Under Maintenance",
        field: "UnderMaintenanceCount",
        filterTemplate: "MultiSelect",
        ShowMactchMode: false,
        class: "smallwidth",
      },
      {
        header: "Disposed",
        field: "disposed",
        filterTemplate: "MultiSelect",
        ShowMactchMode: false,
        class: "smallwidth",
      },
      // {
      //   header: "Action",
      //   field: "action",
      //   slot: "Action",
      //   frozencolumn: true,
      //   sortable: false,
      //   class: "filterdisplaynone last-column-position",
      //   fieldDisplay: "false",
      // },
    ];

    const locationHeaders = [
      {
        header: "Location",
        field: "LocationName",
        filterTemplate: "MultiSelect",
        ShowMactchMode: false,
        class: "mediumwidth",
      },
      {
        header: "Total Assets",
        field: "TotalAssetCount",
        filterTemplate: "MultiSelect",
        ShowMactchMode: false,
        class: "smallwidth",
      },
      {
        header: "In Use",
        field: "InUseCount",
        filterTemplate: "MultiSelect",
        ShowMactchMode: false,
        class: "smallwidth",
      },
      {
        header: "Available",
        field: "AssetAvailabilityCount",
        filterTemplate: "MultiSelect",
        ShowMactchMode: false,
        class: "smallwidth",
      },
      {
        header: "In Transit",
        field: "inTransit",
        filterTemplate: "MultiSelect",
        ShowMactchMode: false,
        class: "smallwidth",
      },
      {
        header: "Under Maintenance",
        field: "UnderMaintenanceCount",
        filterTemplate: "MultiSelect",
        ShowMactchMode: false,
        class: "smallwidth",
      },
      // {
      //   header: "Action",
      //   field: "action",
      //   slot: "Action",
      //   frozencolumn: true,
      //   sortable: false,
      //   class: "filterdisplaynone last-column-position",
      //   fieldDisplay: "false",
      // },
    ];

    const insuranceData = ref([""]);

    const warrantyData = ref([""]);

    const transitData = ref([""]);

    const departmentData = ref([""]);

    const locationData = ref([""]);

    const categoryData = ref([""]);

    const closeSidebar = () => {
      emit("close");
    };

    async function IsShow() {
      // alert("a");
      try {
        const dataresponse = await AssetMasterService.getListAssetDashBoard(
          HospitalId,
          ParamValues.value.BranchId,
          ParamValues.value.StoreId
        );
        DetailsData.value = dataresponse["result"];
        departmentData.value = DetailsData.value.AssetAvailabilityByDept;
        locationData.value = DetailsData.value.AssetAvailabilityByLocation;
        categoryData.value = DetailsData.value.AssetCategory;

        const dataresponse1 =
          await AssetMasterService.getListAssetDashBoardDetails(
            HospitalId,
            ParamValues.value.BranchId,
            ParamValues.value.StoreId,
            0
          );
        DetailsData1.value = dataresponse1["result"];
        transitData.value = DetailsData1.value.AssetInTransitDetails;
        warrantyData.value = DetailsData1.value.ExpiringWarrantiesInfo;
        insuranceData.value = DetailsData1.value.InsuredAssetsInfo;
      } catch (error) {
        console.log(error);
      }
    }

    const getTitle = () => {
      if (!props.selectedAsset) return "Asset Details";

      switch (props.selectedAsset.cardType) {
        case "card1":
          return "Total Assets Overview";
        case "card2":
          return "Assets In Use";
        case "card3":
          return "Asset Availability Analysis";
        case "card5":
          return "Assets in Transit";
        case "card7":
          return "Expiring Warranties";
        case "card9":
          return "Insured Assets";
        default:
          return "Asset Details";
      }
    };

    const getStatusClass = (status: string | undefined) => {
      switch (status?.toLowerCase()) {
        case "active":
        case "available":
          return "status-active";
        case "in use":
        case "assigned":
          return "status-in-use";
        case "maintenance":
          return "status-maintenance";
        case "disposed":
          return "status-disposed";
        default:
          return "status-default";
      }
    };

    const formatDate = (date: string | undefined) => {
      if (!date) return null;
      return new Date(date).toLocaleDateString();
    };

    const formatCurrency = (amount: number | undefined) => {
      if (!amount) return null;
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(amount);
    };

    const viewInsuranceDetails = (data: any) => {
      alert(`Viewing insurance details for ${data.assetName}`);
    };

    const updatePolicy = (data: any) => {
      alert(`Updating insurance policy for ${data.assetName}`);
    };

    const viewWarrantyDetails = (data: any) => {
      alert(`Viewing warranty details for ${data.assetName}`);
    };

    const extendWarranty = (data: any) => {
      alert(`Extending warranty for ${data.assetName}`);
    };

    const viewAssetDetails = (data: any) => {
      alert(`Viewing details for ${data.assetName}`);
    };

    const manageTransfer = (data: any) => {
      alert(`Managing transfer for ${data.assetName}`);
    };

    const viewAssets = (data: any) => {
      alert(`Viewing assets for ${data.department} department`);
    };

    const reallocateAssets = (data: any) => {
      alert(`Reallocating assets for ${data.department} department`);
    };

    const viewLocationAssets = (data: any) => {
      alert(`Viewing assets for ${data.location}`);
    };

    return {
      t,
      insuranceHeaders,
      warrantyHeaders,
      transitHeaders,
      departmentHeaders,
      locationHeaders,
      insuranceData,
      warrantyData,
      transitData,
      departmentData,
      locationData,
      categoryData,
      closeSidebar,
      getTitle,
      getStatusClass,
      formatDate,
      formatCurrency,
      viewInsuranceDetails,
      updatePolicy,
      viewWarrantyDetails,
      extendWarranty,
      viewAssetDetails,
      manageTransfer,
      viewAssets,
      reallocateAssets,
      viewLocationAssets,
      onMounted,
      DetailsData,
    };
  },
});
</script>

<style scoped>
.asset-details-sidebar {
  position: fixed;
  top: 0;
  right: -600px;
  width: 600px;
  height: 100vh;
  background: #ffffff;
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
  transition: right 0.3s ease;
  z-index: 1000;
  overflow-y: auto;
}

.asset-details-sidebar.sidebar-open {
  right: 0;
}

.sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e9ecef;
  background: #f8f9fa;
  position: sticky;
  top: 0;
  z-index: 10;
}

.sidebar-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
}

.btn-close {
  background: none;
  border: none;
  font-size: 18px;
  color: #6c757d;
  cursor: pointer;
  padding: 5px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.btn-close:hover {
  background-color: #e9ecef;
  color: #495057;
}

.sidebar-content {
  padding: 20px;
}

.table-section {
  margin-bottom: 30px;
}

.table-title {
  font-size: 16px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 15px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e9ecef;
}

.action-buttons {
  display: flex;
  gap: 5px;
}

.asset-info-section {
  margin-bottom: 24px;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 12px;
  padding-bottom: 6px;
  border-bottom: 1px solid #e9ecef;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f1f3f4;
}

.info-item:last-child {
  border-bottom: none;
}

.info-item label {
  font-size: 13px;
  color: #6c757d;
  font-weight: 500;
  margin: 0;
  flex: 1;
}

.info-item span {
  font-size: 13px;
  color: #495057;
  text-align: right;
  flex: 1;
}

.status-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}

.status-active {
  background-color: #d4edda;
  color: #155724;
}

.status-in-use {
  background-color: #d1ecf1;
  color: #0c5460;
}

.status-maintenance {
  background-color: #fff3cd;
  color: #856404;
}

.status-disposed {
  background-color: #f8d7da;
  color: #721c24;
}

.status-default {
  background-color: #e2e3e5;
  color: #383d41;
}

.sidebar-actions {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
}

.btn {
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background-color: #007bff;
  border-color: #007bff;
  color: white;
}

.btn-primary:hover {
  background-color: #0056b3;
  border-color: #0056b3;
}

.btn-secondary {
  background-color: #6c757d;
  border-color: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background-color: #545b62;
  border-color: #545b62;
}

.btn-outline-secondary {
  color: #6c757d;
  border-color: #6c757d;
  background-color: transparent;
}

.btn-outline-secondary:hover {
  background-color: #6c757d;
  color: white;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 12px;
}

.me-1 {
  margin-right: 4px;
}

.me-2 {
  margin-right: 8px;
}

.mt-4 {
  margin-top: 24px;
}

.no-selection {
  text-align: center;
  padding: 40px 20px;
  color: #6c757d;
}

.no-selection i {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.no-selection p {
  margin: 0;
  font-size: 14px;
}

.table-container {
  max-height: calc(100vh - 120px);
  overflow-y: auto;
}

.overview-content {
  padding: 20px 0;
}

.overview-stats {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}

.stat-card {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  border: 1px solid #e9ecef;
}

.stat-card h6 {
  margin: 0 0 8px 0;
  font-size: 14px;
  color: #6c757d;
  font-weight: 500;
}

.stat-number {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  color: #2c3e50;
}

.inuse-content {
  padding: 20px 0;
}

.inuse-stats {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}

.category-chart-data {
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
}

.category-item {
  margin-bottom: 16px;
}

.category-item:last-child {
  margin-bottom: 0;
}

.category-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.category-name {
  font-size: 13px;
  font-weight: 500;
  color: #495057;
}

.category-percentage {
  font-size: 13px;
  font-weight: 600;
  color: #2c3e50;
}

/* Responsive Design */
@media (max-width: 768px) {
  .asset-details-sidebar {
    width: 100%;
    right: -100%;
  }

  .sidebar-content {
    padding: 16px;
  }

  .overview-stats,
  .inuse-stats {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .stat-card {
    padding: 16px;
  }

  .stat-number {
    font-size: 24px;
  }

  .action-buttons {
    flex-direction: column;
    gap: 8px;
  }

  .action-buttons .btn {
    width: 100%;
  }
}

/* Enhanced Table Styling */
:deep(.p-datatable) {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
}

:deep(.p-datatable .p-datatable-header) {
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  padding: 12px 16px;
}

:deep(.p-datatable .p-datatable-thead > tr > th) {
  background: #f8f9fa;
  color: #495057;
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 12px 16px;
  border-bottom: 2px solid #e9ecef;
}

:deep(.p-datatable .p-datatable-tbody > tr > td) {
  padding: 12px 16px;
  font-size: 13px;
  color: #495057;
  border-bottom: 1px solid #f1f3f4;
}

:deep(.p-datatable .p-datatable-tbody > tr:hover) {
  background-color: #f8f9fa;
}

:deep(.p-datatable .p-datatable-tbody > tr:last-child > td) {
  border-bottom: none;
}

/* Progress Bar Styling */
:deep(.p-progressbar) {
  background: #e9ecef;
  border-radius: 6px;
  overflow: hidden;
}

:deep(.p-progressbar .p-progressbar-value) {
  background: linear-gradient(90deg, #007bff 0%, #0056b3 100%);
  border-radius: 6px;
  transition: width 0.3s ease;
}

/* Enhanced Button Styling */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  white-space: nowrap;
  user-select: none;
  transition: all 0.15s ease-in-out;
}

.btn:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.25);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Loading State */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e9ecef;
  border-top: 3px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Scrollbar Styling */
.asset-details-sidebar::-webkit-scrollbar {
  width: 6px;
}

.asset-details-sidebar::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.asset-details-sidebar::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.asset-details-sidebar::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Animation for sidebar */
@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.sidebar-open {
  animation: slideIn 0.3s ease-out;
}

/* Enhanced Status Badges */
.status-badge {
  position: relative;
  overflow: hidden;
}

.status-badge::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
  transition: left 0.5s;
}

.status-badge:hover::before {
  left: 100%;
}

/* Card Hover Effects */
.stat-card {
  transition: all 0.3s ease;
  cursor: pointer;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-color: #007bff;
}

/* Table Action Button Enhancements */
.action-buttons .btn {
  position: relative;
  overflow: hidden;
}

.action-buttons .btn::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  transition: width 0.3s, height 0.3s;
}

.action-buttons .btn:hover::before {
  width: 100px;
  height: 100px;
}

/* Enhanced Category Chart */
.category-chart-data {
  border: 1px solid #e9ecef;
  transition: all 0.3s ease;
}

.category-chart-data:hover {
  border-color: #007bff;
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.1);
}

.category-item {
  transition: all 0.2s ease;
  padding: 8px;
  border-radius: 4px;
}

.category-item:hover {
  background: rgba(0, 123, 255, 0.05);
}

/* Print Styles */
@media print {
  .asset-details-sidebar {
    position: static;
    width: 100%;
    height: auto;
    box-shadow: none;
    background: white;
  }

  .sidebar-header {
    background: white;
    border-bottom: 2px solid #000;
  }

  .btn-close {
    display: none;
  }

  .action-buttons {
    display: none;
  }
}
</style>
